import React, { useState, useMemo, useCallback } from 'react';
import Table from '../../components/Table/Table';
import { Employee, TableColumn } from '../../types';
import { exportToExcel, printAsExcel } from '../../utils/exportToExcel';
import { useEmployees, useUpdateEmployee, useDeleteEmployee } from '../../hooks/useEmployees';
import { useBranches } from '../../hooks/useBranches';
import { useRanks } from '../../hooks/useRanks';
import { getArabicRankName } from '../../utils/rankUtils';

export const DeactivatedEmployees: React.FC = () => {
  // API Hooks
  const { data: allEmployees = [], isLoading, error } = useEmployees();
  const { data: branches = [] } = useBranches();
  const { data: ranks = [] } = useRanks();
  const updateEmployeeMutation = useUpdateEmployee();
  const deleteEmployeeMutation = useDeleteEmployee();

  // Filter for inactive employees only
  const employees = useMemo(() => 
    allEmployees.filter((emp: Employee) => emp.status === 'inactive'),
    [allEmployees]
  );

  const [searchTerm, setSearchTerm] = useState('');
  const [userRole] = useState('admin'); // This would come from auth context

  const getBranchName = useCallback((branchId: string) => {
    const branch = branches.find((b) => b.id === branchId);
    return branch ? branch.name : '-';
  }, [branches]);

  // Filtered employees
  const filteredEmployees = useMemo(() => {
    return employees.filter(employee => {
      const branchName = getBranchName(employee.branchId);
      
      // Search filter
      const matchesSearch = !searchTerm || (
        employee.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        employee.arabicName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        employee.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        employee.rank.toLowerCase().includes(searchTerm.toLowerCase()) ||
        employee.position.toLowerCase().includes(searchTerm.toLowerCase()) ||
        employee.nationalId.toLowerCase().includes(searchTerm.toLowerCase()) ||
        branchName.toLowerCase().includes(searchTerm.toLowerCase())
      );
      
      return matchesSearch;
    });
  }, [employees, searchTerm, getBranchName]);

  const handleReactivate = async (employee: Employee) => {
    if (window.confirm(`Are you sure you want to reactivate ${employee.name}?`)) {
      await updateEmployeeMutation.execute({
        id: employee.documentId || employee.id, // Use documentId for proper API calls
        data: { 
          status: 'active',
          documentId: employee.documentId
        }
      });
      alert(`${employee.name} has been reactivated and moved back to active employees.`);
    }
  };

  const handlePermanentDelete = async (employee: Employee) => {
    if (window.confirm(`Are you sure you want to permanently delete ${employee.name}? This action cannot be undone.`)) {
      await deleteEmployeeMutation.execute(employee.documentId || employee.id);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg text-gray-500">Loading deactivated employees...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg text-red-500">Error loading deactivated employees. Please try again.</div>
      </div>
    );
  }

  // Export functionality
  const handleExport = () => {
    const exportData = filteredEmployees.map(emp => ({
      Code: emp.code,
      Name: emp.name,
      'Arabic Name': emp.arabicName,
      Rank: emp.rank,
      'الوظيفة': getArabicRankName(ranks, emp.rank, emp.position),
      'National ID': emp.nationalId,
      'Card Expiry': emp.cardExpiryDate,
      Address: emp.address,
      'Birth Date': emp.birthDate,
      'Seaman Book Number': emp.seamanBookNumber,
      'Seaman Book Expiry': emp.seamanBookExpiry,
      Nationality: emp.nationality,
      'Travel Permit': emp.travelPermit ? 'Yes' : 'No',
      'Phone Number': emp.phoneNumber,
      ...(userRole === 'admin' && { Salary: emp.salary }),
      'Amount (Allowance)': emp.amount,
      'Vessel/Headquarters': getBranchName(emp.branchId),
      'Hire Date': emp.hireDate
    }));
    
    exportToExcel(exportData, `deactivated_employees_${new Date().toISOString().split('T')[0]}`);
  };

  const handlePrint = () => {
    const exportData = filteredEmployees.map(emp => ({
      Code: emp.code,
      Name: emp.name,
      'Arabic Name': emp.arabicName,
      Rank: emp.rank,
      'الوظيفة': getArabicRankName(ranks, emp.rank, emp.position),
      'National ID': emp.nationalId,
      'Card Expiry': emp.cardExpiryDate,
      Address: emp.address,
      'Birth Date': emp.birthDate,
      'Seaman Book Number': emp.seamanBookNumber,
      'Seaman Book Expiry': emp.seamanBookExpiry,
      Nationality: emp.nationality,
      'Travel Permit': emp.travelPermit ? 'Yes' : 'No',
      'Phone Number': emp.phoneNumber,
      Salary: emp.salary,
      Amount: emp.amount
    }));
    
    printAsExcel(exportData, 'Deactivated Employees');
  };

  const columns: TableColumn[] = [
    { key: 'code', label: 'Code', sortable: true },
    { key: 'name', label: 'Name', sortable: true },
    { key: 'arabicName', label: 'الاسم', sortable: true },
    { key: 'rank', label: 'Rank', sortable: true },
    { key: 'position', label: 'الوظيفة', sortable: false, render: (_value: string, item: Employee) => getArabicRankName(ranks, item.rank, item.position) },
    { key: 'nationalId', label: 'الرقم القومي', sortable: true },
    { key: 'cardExpiryDate', label: 'تاريخ انتهاء البطاقة', sortable: true, render: (value: string) => value ? new Date(value).toLocaleDateString() : '-' },
    { key: 'address', label: 'العنوان', sortable: true },
    { key: 'birthDate', label: 'Date of birth', sortable: true, render: (value: string) => value ? new Date(value).toLocaleDateString() : '-' },
    { key: 'birthDate', label: 'تاريخ الميلاد', sortable: true, render: (value: string) => value ? new Date(value).toLocaleDateString('ar-EG') : '-' },
    { key: 'seamanBookNumber', label: 'Seaman\'s book number', sortable: true },
    { key: 'seamanBookNumber', label: 'رقم الجواز البحري', sortable: true },
    { key: 'seamanBookExpiry', label: 'Expiry date of seaman\'s book', sortable: true, render: (value: string) => value ? new Date(value).toLocaleDateString() : '-' },
    { key: 'seamanBookExpiry', label: 'تاريخ انتهاء الجواز البحري', sortable: true, render: (value: string) => value ? new Date(value).toLocaleDateString('ar-EG') : '-' },
    { key: 'nationality', label: 'Nationality', sortable: true },
    { key: 'nationality', label: 'الجنسية', sortable: true },
    { key: 'travelPermit', label: 'Travel permit', sortable: true, render: (value: boolean) => (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${value ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
        {value ? 'Yes' : 'No'}
      </span>
    ) },
    { key: 'phoneNumber', label: 'Mobile number', sortable: false },
    ...(userRole !== 'hr' ? [{ key: 'salary', label: 'Salary', sortable: true, render: (value: number | null) => `$${(value ?? 0).toLocaleString()}` }] : []),
    { key: 'amount', label: 'Amount (Allowance)', sortable: true, render: (value: number | null) => `$${(value ?? 0).toLocaleString()}` }
  ];

  return (
    <div className="space-y-6">
      <div className="text-center space-y-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Deactivated Employees</h1>
          <p className="text-lg text-gray-600 mt-2">Manage inactive employees and reactivation</p>
        </div>
      </div>

      {/* Employee Directory Card */}
      <div className="card">
        <div className="p-6 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
            <h2 className="text-xl font-semibold text-gray-900">Deactivated Employee Directory</h2>
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
              <input
                type="text"
                placeholder="Search employees..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:w-64"
              />
              <button 
                onClick={handleExport}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                Export
              </button>
              <button 
                onClick={handlePrint}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Print
              </button>
            </div>
          </div>
        </div>

        <Table
          columns={columns}
          data={filteredEmployees}
          onEdit={handleReactivate}
          onDelete={handlePermanentDelete}
          title="Deactivated Employee Directory"
          showAddButton={false}
          searchable={false}
        />
      </div>
    </div>
  );
};
